import type { AnalysisResponse } from '../interfaces/analysisResponse';
import type { SentimentResponse } from '../interfaces/sentimentResponse';
import type {
  AnalyzeResultResponse,
  AnalyzeStatusResponse,
  AnalyzeStoryAsyncApiResponse,
} from '../interfaces/storyAnalysis.interface';
import { ensureJsonResponse } from './ensureJsonResponse';
import { sleep } from './sleep.helper';

export interface TriggerAnalysisArgs {
  storyText: string;
  articleSources: string[];
  verifySources: boolean;
}

export interface TriggerAnalysisResult {
  analysis: AnalysisResponse;
  sentiment: SentimentResponse | null;
}

async function analyzeStoryHttp(
  baseUrl: string,
  storyText: string,
  articleSources: string[],
  verifySources: boolean,
): Promise<AnalysisResponse> {
  // Short stories → sync endpoint; long stories → async job flow.
  // Mirrors useStoryAnalyzer.analyzeStory (client/src/hooks/useStoryAnalyzer.tsx).

  if (storyText.length < 1500) {
    const resp = await fetch(`${baseUrl}/api/analyze-story`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        story: storyText,
        user_sources: articleSources,
        verify_sources: verifySources,
      }),
    });

    if (!resp.ok) {
      let message = 'Analysis failed';
      try {
        const errJson = await resp.json();
        if (errJson && typeof errJson === 'object' && 'error' in errJson) {
          message = (errJson as any).error ?? message;
        }
      } catch {
        /* ignore parse error */
      }
      throw new Error(message);
    }

    return ensureJsonResponse<AnalysisResponse>(resp, 'sync analysis');
  }

  // Long story: start async job
  const startResp = await fetch(`${baseUrl}/api/analyze-story-async`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      story: storyText,
      user_sources: articleSources,
      verify_sources: verifySources,
    }),
  });

  if (!startResp.ok) {
    let message = 'Analysis failed';
    try {
      const errJson = await startResp.json();
      if (errJson && typeof errJson === 'object' && 'error' in errJson) {
        message = (errJson as any).error ?? message;
      }
    } catch {
      /* ignore parse error */
    }
    throw new Error(message);
  }

  const startPayload = await ensureJsonResponse<AnalyzeStoryAsyncApiResponse>(
    startResp,
    'async job creation',
  );

  const jobId = startPayload.job_id;
  if (!jobId) {
    throw new Error('Analyze API did not return a job_id');
  }

  // Poll status
  const TIMEOUT_MINUTES = 15;
  const pollIntervalMs = 3_000;
  const timeoutMs = TIMEOUT_MINUTES * 60_000;
  const startTime = Date.now();

  while (true) {
    if (Date.now() - startTime > timeoutMs) {
      throw new Error('Analysis timed out');
    }

    const statusResp = await fetch(
      `${baseUrl}/api/analyze-job-status/${jobId}`,
      { method: 'GET' },
    );

    if (!statusResp.ok) {
      throw new Error(`Status check failed: ${statusResp.status}`);
    }

    const statusData = await ensureJsonResponse<AnalyzeStatusResponse>(
      statusResp,
      'job status',
    );

    const status = (statusData.status ?? '').toString().toUpperCase();

    if (status === 'COMPLETE') {
      break;
    }

    if (status === 'FAILED' || status === 'EXPIRED' || status === 'ERROR') {
      throw new Error(
        statusData.error_message ?? `Analysis failed with status ${status}`,
      );
    }

    await sleep(pollIntervalMs);
  }

  // Fetch final result
  const resultResp = await fetch(`${baseUrl}/api/analyze-job-result/${jobId}`, {
    method: 'GET',
  });

  if (!resultResp.ok) {
    throw new Error(`Result fetch failed: ${resultResp.status}`);
  }

  const resultPayload = await ensureJsonResponse<
    AnalyzeResultResponse<AnalysisResponse>
  >(resultResp, 'job result');

  const finalResult = resultPayload.result as AnalysisResponse | undefined;
  if (finalResult === undefined) {
    throw new Error('Analysis result is missing in the response');
  }

  return finalResult;
}

async function getSentimentHttp(
  baseUrl: string,
  storyText: string,
): Promise<SentimentResponse | null> {
  try {
    const response = await fetch(`${baseUrl}/api/get-sentiment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ story: storyText }),
    });

    if (!response.ok) {
      throw new Error('Sentiment analysis failed');
    }

    return await response.json();
  } catch (err) {
    console.error('Sentiment analysis error:', err);
    return null;
  }
}

/**
 * Pure network helper: runs story analysis + sentiment in parallel and returns
 * both results. No React state, no hooks. Mirrors the network logic from
 * PostDraft.handleAnalyze / useStoryAnalyzer for reuse in EditRewrite (T14).
 *
 * State mutation (setDraftTextAnalysis, setShowAnalysisModal, etc.) stays in
 * the caller — this function only does the network round-trip.
 */
export async function triggerAnalysis(
  args: TriggerAnalysisArgs,
): Promise<TriggerAnalysisResult> {
  const baseUrl = import.meta.env.VITE_STORY_ANALYZER_URL ?? '';

  const { storyText, articleSources, verifySources } = args;

  const [analysis, sentiment] = await Promise.all([
    analyzeStoryHttp(baseUrl, storyText, articleSources, verifySources),
    getSentimentHttp(baseUrl, storyText),
  ]);

  return { analysis, sentiment };
}
